import { createContext, useContext, useEffect, useState } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase, isAuthCircuitOpen, retryAuth } from "@/integrations/supabase/client";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isServiceUnavailable: boolean;
  signOut: () => Promise<void>;
  retryConnection: () => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  isLoading: true,
  isServiceUnavailable: false,
  signOut: async () => {},
  retryConnection: () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isServiceUnavailable, setIsServiceUnavailable] = useState<boolean>(false);

  useEffect(() => {
    // Listen for circuit breaker events
    const handleAuthUnavailable = () => {
      console.log('[AuthContext] Service unavailable event received');
      setIsServiceUnavailable(true);
      setIsLoading(false);
    };
    
    window.addEventListener('supabase:auth:unavailable', handleAuthUnavailable);
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, nextSession) => {
        setSession(nextSession);
        setUser(nextSession?.user ?? null);
        setIsLoading(false);
        // If we got a session, service is available
        if (nextSession) {
          setIsServiceUnavailable(false);
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error && isAuthCircuitOpen()) {
        setIsServiceUnavailable(true);
      } else if (session) {
        setIsServiceUnavailable(false);
      }
      setSession(session);
      setUser(session?.user ?? null);
      setIsLoading(false);
    }).catch((error) => {
      console.error('[AuthContext] getSession failed:', error);
      if (isAuthCircuitOpen()) {
        setIsServiceUnavailable(true);
      }
      setIsLoading(false);
    });

    return () => {
      subscription.unsubscribe();
      window.removeEventListener('supabase:auth:unavailable', handleAuthUnavailable);
    };
  }, []);

  const signOut = async () => {
    try {
      await supabase.auth.signOut({ scope: 'global' });
    } catch (error) {
      console.error('[AuthContext] Sign out API failed, clearing local session:', error);
    }
    // Always clear local state regardless of API result
    // This ensures user can sign out even if backend is having issues
    setSession(null);
    setUser(null);
    // Force redirect to auth route (router defines /auth, not /login)
    window.location.href = '/auth';
  };

  const retryConnection = () => {
    setIsServiceUnavailable(false);
    setIsLoading(true);
    retryAuth();
  };

  return (
    <AuthContext.Provider value={{ user, session, isLoading, isServiceUnavailable, signOut, retryConnection }}>
      {children}
    </AuthContext.Provider>
  );
};



